import { ApiError } from '@/api/errors';
import type {
    AccountStatus,
    AccountStatusResult,
    CustomerRoute,
    WizardBlocked,
    WizardSession,
} from './types';

/**
 * ¿La cuenta Pago Confiado ya existe, según la sesión? Recién abierta (`OPEN`) o la que el comprador
 * ya traía (`LINKED`) — el mismo predicado que el silo llama `isAccountReady`.
 */
export function accountAlreadyOpen(status: AccountStatus | undefined): boolean {
    return status === 'OPEN' || status === 'LINKED';
}

/** Lo mínimo de la sesión para saber con qué cuenta llegó el comprador. */
export interface AccountOrigin {
    route: CustomerRoute | undefined;
    accountStatus: AccountStatus | undefined;
}

/**
 * ¿El comprador YA TRAÍA su cuenta Pago Confiado, en vez de que se la abriéramos nosotros?
 *
 * ⚠️ NO es `accountAlreadyOpen`: esa pregunta si la cuenta EXISTE y dice que sí también para el
 * `OPEN` que abre el KYC completo, a quien sí hay que instalarle la app y darle su tarjeta.
 *
 * Mismo corte que el silo usa para el bono de bienvenida: ruta facematch o cuenta enlazada. Con uno
 * alcanza — la ruta llega `undefined` hasta que la bifurcación se resuelve.
 */
export function alreadyHadAccount(origin: AccountOrigin | undefined): boolean {
    return origin?.route === 'FACEMATCH' || origin?.accountStatus === 'LINKED';
}

/** El origen de la cuenta, leído de la sesión del wizard. */
export function accountOrigin(
    session: Pick<WizardSession, 'kyc' | 'accountStatus'>,
): AccountOrigin {
    return { route: session.kyc.route, accountStatus: session.accountStatus };
}

/** Los primeros segundos, que es donde cae el caso feliz. */
export const ACCOUNT_POLL_MS_FAST = 2000;
/** El resto de la espera, cuando ya no es cuestión de segundos. */
export const ACCOUNT_POLL_MS_SLOW = 5000;
/** ~30 s a `ACCOUNT_POLL_MS_FAST`. */
export const ACCOUNT_POLL_FAST_ROUNDS = 15;
/** Mientras se ve el letrero amarillo de cuenta no lista, sin tope de vueltas. */
export const ACCOUNT_POLL_MS_BLOCKED = 10_000;

/** Qué letrero de venta trabada tiene el vendedor enfrente, si alguno. */
export type BlockedNotice = 'NONE' | 'PENDING' | 'REJECTED';

/** El letrero que se pinta: el de rechazo solo con la categoría `REJECTED`, igual que `blockedCopy`. */
export function blockedNoticeOf(
    saleBlocked: boolean,
    blocked: WizardBlocked | null | undefined,
): BlockedNotice {
    if (!saleBlocked) return 'NONE';
    return blocked?.category === 'REJECTED' ? 'REJECTED' : 'PENDING';
}

/**
 * Cada cuánto repreguntar por la cuenta. Con el letrero amarillo, cada 10 s sin importar el
 * desenlace; con el de rechazo, nunca; en la espera normal arranca rápido y afloja.
 */
export function accountPollInterval(
    waiting: boolean,
    rounds: number,
    notice: BlockedNotice = 'NONE',
): number | false {
    if (notice === 'REJECTED') return false;
    if (notice === 'PENDING') return ACCOUNT_POLL_MS_BLOCKED;
    if (!waiting) return false;
    return rounds < ACCOUNT_POLL_FAST_ROUNDS ? ACCOUNT_POLL_MS_FAST : ACCOUNT_POLL_MS_SLOW;
}

/**
 * ¿La consulta en sí está rota, más allá de lo que diga la cuenta?
 *
 * Un 4xx no se arregla repitiéndolo: la sesión no tiene con qué consultar, o el request no procede.
 * Los 5xx y los cortes de red sí se reintentan, que para eso el poll insiste.
 */
export function accountLookupBlocked(error: unknown): boolean {
    return error instanceof ApiError && error.httpStatus >= 400 && error.httpStatus < 500;
}

/**
 * Margen antes de creerle a un `MISSING_INFO`. La emisión arranca recién cuando se manda la
 * profesión, así que las primeras vueltas del poll llegan con el expediente a medio escribir.
 */
export const ACCOUNT_MISSING_INFO_GRACE_MS = 20_000;

/**
 * ¿El expediente incompleto ya es un estatus firme y no el hueco del arranque?
 *
 * `null` = el cronómetro no arrancó, así que todavía no se puede afirmar nada.
 */
export function missingInfoConfirmed(
    status: AccountStatusResult | undefined,
    msWaiting: number | null,
): boolean {
    if (status?.outcome !== 'MISSING_INFO') return false;
    return msWaiting !== null && msWaiting >= ACCOUNT_MISSING_INFO_GRACE_MS;
}

/**
 * ¿Tiene sentido seguir repreguntando por la cuenta?
 *
 * Se corta en los desenlaces que no se revierten solos: la cuenta existe, el onboarding no la puede
 * abrir, falta información que nadie va a mandar desde esta pantalla, o la consulta está rota.
 */
export function stillWaitingForAccount(
    status?: AccountStatusResult,
    error?: unknown,
    msWaiting: number | null = null,
): boolean {
    if (accountLookupBlocked(error)) return false;
    if (!status) return true;
    if (status.accountReady) return false;

    switch (status.outcome) {
        case 'READY':
        case 'CANNOT_OPEN':
            return false;
        case 'MISSING_INFO':
            return !missingInfoConfirmed(status, msWaiting);
        default:
            return true;
    }
}

/** Tope de la espera. Generoso a propósito: cortar de más bloquea una venta sana. */
export const ACCOUNT_WAIT_MAX_MS = 5 * 60_000;

/** `null` = el cronómetro no arrancó, así que no hay espera que agotar. */
export function accountWaitTimedOut(msWaiting: number | null): boolean {
    if (msWaiting === null) return false;
    return msWaiting >= ACCOUNT_WAIT_MAX_MS;
}

/**
 * ¿La apertura ya se dio por perdida? El rechazo del onboarding y la espera agotada tienen la misma
 * consecuencia: la venta no avanza al catálogo.
 */
export function accountFailedToOpen(
    status: AccountStatusResult | undefined,
    timedOut: boolean,
): boolean {
    if (status?.accountReady) return false;
    if (status?.outcome === 'CANNOT_OPEN') return true;
    // Con el expediente incompleto el cronómetro no decide: el estatus certero es qué falta.
    if (status?.outcome === 'MISSING_INFO') return false;
    return timedOut;
}

/**
 * Cuánto se espera antes de ofrecerle salir al vendedor. Con 13 s de mediana medidos en Dev, salir
 * antes fabrica seguimiento para una venta que estaba por resolverse sola.
 */
export const ACCOUNT_EXIT_LOCK_MS = 30_000;

/** `null` = el cronómetro no arrancó, así que todavía no pasó ningún segundo. */
export function exitOfferedWhileOpening(msWaiting: number | null): boolean {
    return msWaiting !== null && msWaiting >= ACCOUNT_EXIT_LOCK_MS;
}

/**
 * ¿Este bloqueo corta la venta sin esperar a que el paso 02 termine de pedir lo que le falta?
 *
 * Cortan las dos que traen un veredicto firme del otro lado: `UNDER_REVIEW` —alguien ya miró el caso
 * y dijo que no procede— y `REJECTED`, que además ya no vuelve. Pedirle un recibo ahí le hace perder
 * el tiempo al cliente sin cambiar el resultado. Las otras dos esperan: `CUSTOMER_RECORD` es «falta
 * información del expediente», y el domicilio ES información del expediente; `TEMPORARY` es que la
 * fuente no contestó, o sea que todavía no sabemos nada y decir «no se pudo abrir» manda a buscar
 * donde no es.
 *
 * Una categoría que este front todavía no conoce espera también: mandar al comprobante de más
 * cuesta un rodeo, y dar la venta por perdida de más cuesta la venta.
 */
export function blockCutsBeforeStep02(blocked: WizardBlocked | null | undefined): boolean {
    if (blocked?.reason !== 'ACCOUNT_BLOCKED') return false;
    return blocked.category === 'UNDER_REVIEW' || blocked.category === 'REJECTED';
}

/** Todo lo que decide si la venta está trabada por la cuenta, junto en un lugar. */
export interface AccountBlockSignals {
    /** Lo que el silo escribió en la sesión. Manda sobre las señales locales. */
    blocked: WizardBlocked | null | undefined;
    /** El expediente ya viajó ⇒ el poll de la cuenta corre y sus señales valen. */
    waiting: boolean;
    /** La cuenta ya existe: no hay nada que trabar. */
    accountReady: boolean;
    lookupError: unknown;
    status: AccountStatusResult | undefined;
    msWaiting: number | null;
    waitExpired: boolean;
    /**
     * Al paso 02 le queda algo que pedirle al cliente: el comprobante de domicilio o las preguntas
     * del perfil. Mientras lo tenga, un expediente incompleto no es un bloqueo.
     */
    canStillCollect: boolean;
}

/**
 * ¿La venta se detiene aquí? Las cuatro señales convergen en el mismo aviso. Las locales se
 * conservan además del `blocked` del silo, que puede ir un despliegue atrás.
 *
 * 🔴 Mientras al paso 02 le quede algo que pedir, la única señal que corta es el rechazo firme del
 * emisor (`UNDER_REVIEW` / `CANNOT_OPEN`). El resto describe un expediente a medias o una fuente que
 * no contestó, y las dos pantallas que quedan —el comprobante y las preguntas del perfil— son
 * justamente lo que puede completarlo. Ver `blockCutsBeforeStep02`.
 */
export function accountBlocksTheSale(signals: AccountBlockSignals): boolean {
    if (signals.accountReady) return false;

    // El rechazo firme del emisor corta siempre, por las dos vías en que llega.
    if (blockCutsBeforeStep02(signals.blocked)) return true;
    if (signals.waiting && signals.status?.outcome === 'CANNOT_OPEN') return true;

    // Todo lo demás espera: mientras el paso tenga qué pedir, esa pantalla es la que manda.
    if (signals.canStillCollect) return false;

    if (signals.blocked?.reason === 'ACCOUNT_BLOCKED') return true;
    if (!signals.waiting) return false;
    return (
        accountLookupBlocked(signals.lookupError) ||
        missingInfoConfirmed(signals.status, signals.msWaiting) ||
        accountFailedToOpen(signals.status, signals.waitExpired)
    );
}
